/**
 * Navigation Management Utilities
 *
 * Tree operations for NavigationNode: flatten, sort, find, validate,
 * active-trail resolution, and local draft management.
 */

import type {
  NavigationNode,
  NavigationDraftSnapshot,
  NavigationGuard,
  NavigationNodeKind,
  NavigationPortalType,
  NavigationValidationSeverity,
} from '@/types/navigation_management';
import { substituteRuntimeRouteTokens } from './runtime_route_paths';

// ─── Node Creation ─────────────────────────────────────────────

let nextSyntheticId = 1;

const syntheticIdPrefix = 'nav-synthetic-';

export const createSyntheticNodeId = (): string =>
  `${syntheticIdPrefix}${nextSyntheticId++}`;

export const isSyntheticNodeId = (id: string): boolean =>
  id.startsWith(syntheticIdPrefix);

export const createNavigationNode = (
  overrides: Partial<NavigationNode> & Pick<NavigationNode, 'moduleCode' | 'key' | 'portalType'>
): NavigationNode => ({
  id: overrides.id ?? createSyntheticNodeId(),
  moduleCode: overrides.moduleCode,
  portalType: overrides.portalType,
  kind: overrides.kind ?? 'link',
  key: overrides.key,
  label: overrides.label,
  iconKey: overrides.iconKey,
  routeId: overrides.routeId,
  routePath: overrides.routePath,
  externalHref: overrides.externalHref,
  externalTarget: overrides.externalTarget ?? '_blank',
  order: overrides.order ?? 0,
  enabled: overrides.enabled ?? true,
  showInNavigation: overrides.showInNavigation ?? true,
  badge: overrides.badge,
  guards: overrides.guards ?? [],
  children: overrides.children ?? [],
  metadata: overrides.metadata,
});

export const createNavigationGuard = (
  type: NavigationGuard['type'],
  value?: string
): NavigationGuard => ({ type, value });

export const createNavigationDraftSnapshot = (
  moduleCode: string,
  nodes: NavigationNode[] = []
): NavigationDraftSnapshot => ({
  versionId: null,
  version: 0,
  checksum: null,
  status: 'DRAFT',
  nodes,
  updatedAt: new Date().toISOString(),
  updatedBy: null,
  message: null,
});

// ─── Tree Traversal ────────────────────────────────────────────

export const flattenNavigationNodes = (
  nodes: NavigationNode[]
): NavigationNode[] =>
  nodes.flatMap((node) => [
    node,
    ...flattenNavigationNodes(node.children),
  ]);

export const countNavigationNodes = (nodes: NavigationNode[]): number =>
  flattenNavigationNodes(nodes).length;

export const findNavigationNodeById = (
  nodes: NavigationNode[],
  nodeId: string
): NavigationNode | null => {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }
    const found = findNavigationNodeById(node.children, nodeId);
    if (found) {
      return found;
    }
  }
  return null;
};

export const findNavigationNodeByKey = (
  nodes: NavigationNode[],
  key: string
): NavigationNode | null => {
  const normalizedKey = key.trim().toLowerCase();
  for (const node of nodes) {
    if (node.key.trim().toLowerCase() === normalizedKey) {
      return node;
    }
    const found = findNavigationNodeByKey(node.children, key);
    if (found) {
      return found;
    }
  }
  return null;
};

export const findNavigationNodeByRoutePath = (
  nodes: NavigationNode[],
  routePath: string
): NavigationNode | null => {
  const normalizedPath = routePath.trim().toLowerCase();
  for (const node of nodes) {
    if (node.routePath && node.routePath.trim().toLowerCase() === normalizedPath) {
      return node;
    }
    const found = findNavigationNodeByRoutePath(node.children, routePath);
    if (found) {
      return found;
    }
  }
  return null;
};

// ─── Ancestor/Path Resolution ──────────────────────────────────

export const findAncestorPath = (
  nodes: NavigationNode[],
  targetNodeId: string
): NavigationNode[] => {
  const path: NavigationNode[] = [];

  const search = (currentNodes: NavigationNode[]): boolean => {
    for (const node of currentNodes) {
      if (node.id === targetNodeId) {
        path.push(node);
        return true;
      }
      path.push(node);
      if (search(node.children)) {
        return true;
      }
      path.pop();
    }
    return false;
  };

  search(nodes);
  return path;
};

export const isDescendantOf = (
  nodes: NavigationNode[],
  ancestorId: string,
  descendantId: string
): boolean => {
  const ancestor = findNavigationNodeById(nodes, ancestorId);
  if (!ancestor) {
    return false;
  }
  return findNavigationNodeById(ancestor.children, descendantId) !== null;
};

// ─── Tree Mutation ─────────────────────────────────────────────

export const addRootNavigationNode = (
  nodes: NavigationNode[],
  node: NavigationNode
): NavigationNode[] => [...nodes, node];

export const addChildNavigationNode = (
  nodes: NavigationNode[],
  parentId: string,
  child: NavigationNode
): NavigationNode[] =>
  nodes.map((node) => {
    if (node.id === parentId) {
      return { ...node, children: [...node.children, child] };
    }
    return { ...node, children: addChildNavigationNode(node.children, parentId, child) };
  });

export const updateNavigationNode = (
  nodes: NavigationNode[],
  nodeId: string,
  updater: (node: NavigationNode) => NavigationNode
): NavigationNode[] =>
  nodes.map((node) => {
    if (node.id === nodeId) {
      return updater(node);
    }
    return { ...node, children: updateNavigationNode(node.children, nodeId, updater) };
  });

export const removeNavigationNode = (
  nodes: NavigationNode[],
  nodeId: string
): NavigationNode[] =>
  nodes
    .filter((node) => node.id !== nodeId)
    .map((node) => ({
      ...node,
      children: removeNavigationNode(node.children, nodeId),
    }));

export const duplicateNavigationNode = (
  nodes: NavigationNode[],
  nodeId: string
): NavigationNode[] => {
  const target = findNavigationNodeById(nodes, nodeId);
  if (!target) {
    return nodes;
  }

  const cloneWithNewIds = (source: NavigationNode): NavigationNode => ({
    ...source,
    id: createSyntheticNodeId(),
    key: `${source.key}-copy`,
    children: source.children.map(cloneWithNewIds),
  });

  const clone = cloneWithNewIds(target);
  const parentIndex = nodes.findIndex((node) => node.id === nodeId);

  if (parentIndex >= 0) {
    return [...nodes.slice(0, parentIndex + 1), clone, ...nodes.slice(parentIndex + 1)];
  }

  return nodes.map((node) => ({
    ...node,
    children: addChildAfterId(node.children, nodeId, clone),
  }));
};

const addChildAfterId = (
  nodes: NavigationNode[],
  targetId: string,
  child: NavigationNode
): NavigationNode[] => {
  const index = nodes.findIndex((node) => node.id === targetId);
  if (index >= 0) {
    return [...nodes.slice(0, index + 1), child, ...nodes.slice(index + 1)];
  }
  return nodes.map((node) => ({
    ...node,
    children: addChildAfterId(node.children, targetId, child),
  }));
};

// ─── Sorting ───────────────────────────────────────────────────

const compareNavigationNodes = (left: NavigationNode, right: NavigationNode) => {
  if (left.order !== right.order) {
    return left.order - right.order;
  }
  const leftKey = left.key.trim().toLowerCase();
  const rightKey = right.key.trim().toLowerCase();
  return leftKey.localeCompare(rightKey, 'en', { sensitivity: 'base' });
};

export const sortNavigationNodes = (nodes: NavigationNode[]): NavigationNode[] =>
  [...nodes]
    .sort(compareNavigationNodes)
    .map((node) => ({
      ...node,
      children: sortNavigationNodes(node.children),
    }));

// ─── Resequence ────────────────────────────────────────────────

export const resequenceNavigationNodes = (
  nodes: NavigationNode[],
  startOrder = 100,
  step = 10
): NavigationNode[] =>
  sortNavigationNodes(nodes).map((node, index) => ({
    ...node,
    order: startOrder + index * step,
    children: resequenceNavigationNodes(node.children, startOrder, step),
  }));

// ─── Filtering by Portal Type ──────────────────────────────────

export const filterNavigationNodesByPortal = (
  nodes: NavigationNode[],
  portalType: NavigationPortalType
): NavigationNode[] =>
  nodes
    .filter((node) => node.portalType === portalType || node.portalType === 'portal')
    .map((node) => ({
      ...node,
      children: filterNavigationNodesByPortal(node.children, portalType),
    }));

// ─── Filtering by Visibility ───────────────────────────────────

export const filterVisibleNavigationNodes = (
  nodes: NavigationNode[]
): NavigationNode[] =>
  nodes
    .filter((node) => node.enabled && node.showInNavigation)
    .map((node) => ({
      ...node,
      children: filterVisibleNavigationNodes(node.children),
    }));

// ─── Filtering by Module Entitlement ───────────────────────────

/**
 * Drop navigation nodes whose module is disabled for the current tenant.
 * A node is gated either by an explicit 'module-entitlement' guard or by its
 * own moduleCode ('core' and empty codes are never gated). The verdict
 * callback returns false only when the entitlement state is KNOWN and the
 * module is inactive — unknown/loading states keep the node visible.
 */
export const filterNavigationNodesByModuleEntitlement = (
  nodes: NavigationNode[],
  isModuleEntitled: (moduleCode: string) => boolean
): NavigationNode[] =>
  nodes
    .map((node) => ({
      ...node,
      children: filterNavigationNodesByModuleEntitlement(node.children, isModuleEntitled),
    }))
    .filter((node) => {
      const guard = (node.guards ?? []).find(
        (g) => g.type === 'module-entitlement' && (g.value ?? '').trim().length > 0
      );
      const code = (guard?.value ?? node.moduleCode ?? '').trim();
      if (!code || code === 'core') {
        return true;
      }
      return isModuleEntitled(code);
    });

// ─── Filtering by Permission Guards ────────────────────────────

/**
 * Drop navigation nodes gated by a 'permission' guard (value = "ENTITY.PERMISSION",
 * e.g. "AI_SCAN.VIEW") when the permission verdict is KNOWN and negative.
 * Unknown/loading states keep the node visible (no flicker) — same convention
 * as filterNavigationNodesByModuleEntitlement. Nodes without a permission
 * guard are never affected.
 */
export const filterNavigationNodesByPermissions = (
  nodes: NavigationNode[],
  hasPermission: (permission: string) => boolean
): NavigationNode[] =>
  nodes
    .map((node) => ({
      ...node,
      children: filterNavigationNodesByPermissions(node.children, hasPermission),
    }))
    .filter((node) => {
      const guard = (node.guards ?? []).find(
        (g) => g.type === 'permission' && (g.value ?? '').trim().length > 0
      );
      if (!guard) {
        return true;
      }
      return hasPermission((guard.value ?? '').trim());
    });

// ─── Route Token Substitution ──────────────────────────────────

/**
 * Recursively resolve `:workspaceId` / `:year` tokens in every node's
 * `routePath` and `newItemPath` using the current portal context.
 *
 * Navigation trees are authored with template paths (e.g.
 * `/portal/:workspaceId/:year/ai/scans/new`); the runtime header navigates to
 * `routePath` verbatim, so these tokens must be substituted with the active
 * workspace and year before the nodes are handed to DynamicTopNav. Nodes are
 * returned unchanged when no substitution is needed.
 */
export const substituteNavigationNodeTokens = (
  nodes: NavigationNode[],
  context: { workspaceId?: string | null; year?: string | null }
): NavigationNode[] =>
  nodes.map((node) => {
    const routePath = substituteRuntimeRouteTokens(node.routePath, context);
    const newItemPath = substituteRuntimeRouteTokens(node.newItemPath, context);
    return {
      ...node,
      routePath,
      newItemPath,
      children: substituteNavigationNodeTokens(node.children, context),
    };
  });

// ─── Active Trail Resolution ───────────────────────────────────

export type NavigationActiveTrail = {
  /** The node that exactly matches the current route */
  activeNode: NavigationNode | null;
  /** All ancestor nodes from root to the active node */
  ancestorNodes: NavigationNode[];
  /** Keys of all nodes in the active trail */
  trailKeys: string[];
};

export const resolveNavigationActiveTrail = (
  nodes: NavigationNode[],
  currentPathname: string
): NavigationActiveTrail => {
  const normalizedPath = currentPathname.trim().toLowerCase();

  const search = (
    currentNodes: NavigationNode[],
    ancestors: NavigationNode[]
  ): NavigationActiveTrail => {
    for (const node of currentNodes) {
      const currentAncestors = [...ancestors, node];

      // Check if this node's route matches the current path
      if (node.routePath) {
        const nodeRoute = node.routePath.trim().toLowerCase();
        // Exact match
        if (nodeRoute === normalizedPath) {
          return {
            activeNode: node,
            ancestorNodes: ancestors,
            trailKeys: currentAncestors.map((n) => n.key),
          };
        }
        // Prefix match (current path starts with node route)
        if (
          normalizedPath.startsWith(nodeRoute) &&
          (normalizedPath.length === nodeRoute.length || normalizedPath[nodeRoute.length] === '/')
        ) {
          // Continue searching children for a closer match, but remember this as candidate
          const childResult = search(node.children, currentAncestors);
          if (childResult.activeNode) {
            return childResult;
          }
          return {
            activeNode: node,
            ancestorNodes: ancestors,
            trailKeys: currentAncestors.map((n) => n.key),
          };
        }
      }

      // Check children
      const childResult = search(node.children, currentAncestors);
      if (childResult.activeNode) {
        return childResult;
      }
    }

    return { activeNode: null, ancestorNodes: [], trailKeys: [] };
  };

  return search(nodes, []);
};

// ─── Validation ────────────────────────────────────────────────

export type NavigationClientIssue = {
  id: string;
  code: string;
  severity: NavigationValidationSeverity;
  message: string;
  nodeId: string | null;
};

const issueId = (() => {
  let counter = 0;
  return () => `nav-issue-${++counter}`;
})();

export const validateNavigationSnapshot = (
  snapshot: NavigationDraftSnapshot
): NavigationClientIssue[] => {
  const issues: NavigationClientIssue[] = [];
  const knownIds = new Set<string>();
  const knownKeys = new Set<string>();

  const validateNode = (node: NavigationNode, depth: number) => {
    // Duplicate ID
    if (knownIds.has(node.id)) {
      issues.push({
        id: issueId(),
        code: 'duplicate-id',
        severity: 'error',
        message: `Duplicate node ID: ${node.id}`,
        nodeId: node.id,
      });
    }
    knownIds.add(node.id);

    // Duplicate key at same level
    const keyAtLevel = `${depth}:${node.key}`;
    if (knownKeys.has(keyAtLevel)) {
      issues.push({
        id: issueId(),
        code: 'duplicate-key',
        severity: 'warning',
        message: `Duplicate key "${node.key}" at depth ${depth}`,
        nodeId: node.id,
      });
    }
    knownKeys.add(keyAtLevel);

    // Empty key
    if (!node.key.trim()) {
      issues.push({
        id: issueId(),
        code: 'empty-key',
        severity: 'error',
        message: 'Node key must not be empty.',
        nodeId: node.id,
      });
    }

    // Link without route
    if (node.kind === 'link' && !node.routePath && !node.routeId) {
      issues.push({
        id: issueId(),
        code: 'link-without-route',
        severity: 'warning',
        message: `Link node "${node.key}" has no routePath or routeId.`,
        nodeId: node.id,
      });
    }

    // External link without href
    if (node.kind === 'external-link' && !node.externalHref) {
      issues.push({
        id: issueId(),
        code: 'external-link-without-href',
        severity: 'error',
        message: `External link node "${node.key}" has no externalHref.`,
        nodeId: node.id,
      });
    }

    // Group with no visible children
    if (node.kind === 'group' && node.children.length === 0) {
      issues.push({
        id: issueId(),
        code: 'empty-group',
        severity: 'warning',
        message: `Group node "${node.key}" has no children.`,
        nodeId: node.id,
      });
    }

    // Separator with children
    if (node.kind === 'separator' && node.children.length > 0) {
      issues.push({
        id: issueId(),
        code: 'separator-with-children',
        severity: 'warning',
        message: `Separator node "${node.key}" should not have children.`,
        nodeId: node.id,
      });
    }

    // Max depth
    if (depth > 3) {
      issues.push({
        id: issueId(),
        code: 'max-depth-exceeded',
        severity: 'warning',
        message: `Node "${node.key}" exceeds max navigation depth of 3.`,
        nodeId: node.id,
      });
    }

    // Validate children
    node.children.forEach((child) => validateNode(child, depth + 1));
  };

  snapshot.nodes.forEach((node) => validateNode(node, 0));

  return issues;
};

// ─── Serialization ─────────────────────────────────────────────

export const serializeNavigationDraftSnapshot = (
  snapshot: NavigationDraftSnapshot
): string => JSON.stringify(snapshot);

export const touchNavigationDraftSnapshot = (
  moduleCode: string,
  snapshot: NavigationDraftSnapshot
): NavigationDraftSnapshot => ({
  ...snapshot,
  updatedAt: new Date().toISOString(),
  moduleCode,
});

// ─── Path Parsing ──────────────────────────────────────────────

export type NavigationPathDefinition = {
  portalPrefix: string;
  workspaceId: boolean;
  year: boolean;
  domain: string;
  entity: string;
  suffix: string;
};

export const parseNavigationPathDefinition = (
  path: string,
  fallbackPortalType: NavigationPortalType = 'portal'
): NavigationPathDefinition => {
  const segments = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);

  const portalPrefix = segments[0] ?? fallbackPortalType;
  let index = 1;

  const hasWorkspaceId = segments[index] === ':workspaceId';
  if (hasWorkspaceId) index++;

  const hasYear = segments[index] === ':year';
  if (hasYear) index++;

  const domain = segments[index] ?? '';
  index++;

  const entity = segments[index] ?? '';
  index++;

  const suffix = segments.slice(index).join('/');

  return {
    portalPrefix,
    workspaceId: hasWorkspaceId,
    year: hasYear,
    domain,
    entity,
    suffix,
  };
};

// ─── Portal Prefix Replacement ─────────────────────────────────

export const replaceNavigationPortalPrefix = (
  path: string,
  newPortalType: NavigationPortalType
): string => {
  const segments = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (segments.length === 0) {
    return `/${newPortalType}`;
  }
  const knownPrefixes = new Set(['portal', 'adminportal', 'make']);
  if (knownPrefixes.has(segments[0])) {
    segments[0] = newPortalType;
  } else {
    segments.unshift(newPortalType);
  }
  return `/${segments.join('/')}`;
};

// ─── Route Path Builder ────────────────────────────────────────

export const buildNavigationRoutePath = (options: {
  portalPrefix: NavigationPortalType;
  includeWorkspaceId: boolean;
  includeYear: boolean;
  domain: string;
  entity: string;
  suffix?: string;
}): string => {
  const segments: string[] = [options.portalPrefix];
  if (options.includeWorkspaceId) segments.push(':workspaceId');
  if (options.includeYear) segments.push(':year');
  segments.push(options.domain.trim().toLowerCase());
  segments.push(options.entity.trim().toLowerCase());
  const suffix = options.suffix?.trim();
  if (suffix) segments.push(suffix);
  return `/${segments.join('/')}`;
};
